import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiSearch, FiMapPin, FiBriefcase, FiArrowRight } from "react-icons/fi";
import { API_BASE_URL } from "../utils/constant";

// --- Framer Motion Variants ---
const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
  viewport: { once: true, amount: 0.1 },
};

// --- Helper Functions ---
const truncateHTMLWords = (html, limit) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  const words = text.split(/\s+/);
  if (words.length <= limit) return text;
  return words.slice(0, limit).join(" ") + "…";
};

const getUpdatedText = (dateStr) => {
  if (!dateStr) return "recently";
  try {
    const updatedDate = new Date(dateStr);
    const today = new Date();
    updatedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffInMs = today - updatedDate;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays > 1) return `${diffInDays} days ago`;
    return `In ${Math.abs(diffInDays)} days`;
  } catch (error) {
    return "recently";
  }
};

// --- Stylish SVG Placeholder for Company Logo ---
const CompanyLogoPlaceholder = () => (
    <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </div>
);


// --- Skeleton Component Updated for New Card Layout ---
const JobCardSkeleton = () => (
    <div className="bg-white rounded-xl border border-slate-200/80 p-6 flex items-start gap-6 animate-pulse">
        <div className="flex-shrink-0">
            <div className="w-14 h-14 bg-slate-200 rounded-lg"></div>
        </div>
        <div className="flex-grow space-y-4">
            <div className="h-6 bg-slate-200 rounded w-4/5"></div>
            <div className="flex flex-wrap gap-2">
                <div className="h-5 bg-slate-200 rounded-full w-20"></div>
                <div className="h-5 bg-slate-200 rounded-full w-24"></div>
            </div>
            <div className="space-y-2 pt-2">
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-2/3"></div>
            </div>
        </div>
    </div>
);


function DynamicCareer() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/jobs`);
        if (!response.ok) throw new Error("Network response was not ok.");
        const data = await response.json();
        const mappedJobs = data.map((item) => ({
          id: item.id,
          title: item.title,
          status: item.status === "active" ? "Available" : "Closed",
          skills: item.skills || [],
          description: item.description || "No description provided.",
          updated: item.start_date,
          slug: item.slug,
          location: item.location || 'Kolkata, WB', 
          type: item.type || 'Full-time'
        }));
        setJobs(mappedJobs);
      } catch (error) {
        console.error("Fetch error:", error);
        setError("Failed to load job openings. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const filteredJobs = useMemo(() => {
    return jobs.filter(job =>
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.skills.some(skill => skill.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [jobs, searchTerm]);

  return (
    <main className="bg-slate-50">
      <section className="bg-gray-900 border-b border-gray-700">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
    <motion.h1
      className="text-4xl sm:text-6xl font-extrabold text-white leading-tight tracking-tight"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
    >
      Join Our <span className="text-blue-400">Vision</span>
    </motion.h1>
    <motion.p
      className="mt-6 text-lg max-w-3xl mx-auto text-gray-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      We're looking for passionate innovators and problem-solvers to help us build the future of technology. Explore our open positions and find your next great opportunity.
    </motion.p>
  </div>
</section>

      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* --- Professional Search & Filter Bar --- */}
        <motion.div className="bg-slate-100/80 p-6 rounded-xl border border-slate-200/80 mb-12" {...fadeIn}>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="relative w-full flex-grow">
              <FiSearch className="absolute top-1/2 left-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by job title, skill, or keyword..."
                className="w-full h-12 pl-12 pr-4 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="w-full md:w-auto h-12 px-8 bg-blue-600 text-white font-semibold rounded-lg shadow-sm hover:bg-blue-700 transition-colors">
              Search
            </button>
          </div>
        </motion.div>

        {/* --- Job Listings Section --- */}
        <div className="space-y-6">
            {loading ? (
                <>
                    <JobCardSkeleton />
                    <JobCardSkeleton />
                    <JobCardSkeleton />
                    <JobCardSkeleton />
                </>
            ) : error ? (
                <div className="text-center py-12">
                    <p className="text-lg text-red-600">{error}</p>
                </div>
            ) : filteredJobs.length > 0 ? (
                <motion.div className="space-y-6" {...staggerContainer}>
                {filteredJobs.map((job) => (
                  <motion.div
                    key={job.id}
                    className="group bg-white rounded-xl border border-slate-200/80 p-6 flex flex-col sm:flex-row items-start gap-6 transition-all duration-300 hover:shadow-xl hover:border-blue-300 hover:-translate-y-1"
                    {...fadeIn}
                  >
                    <div className="flex-shrink-0">
                      <CompanyLogoPlaceholder/>
                    </div>
                    <div className="flex-grow">
                        <div className="flex flex-wrap justify-between items-start mb-1">
                            <h2 className="text-xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{job.title}</h2>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                job.status === "Available" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"
                            }`}>
                              {job.status}
                            </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                            <span className="flex items-center gap-1.5"><FiMapPin /> {job.location}</span>
                            <span className="flex items-center gap-1.5"><FiBriefcase /> {job.type}</span>
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed mb-4">
                            {truncateHTMLWords(job.description, 25)}
                        </p>
                         {Array.isArray(job.skills) && job.skills.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                            {job.skills.slice(0, 4).map((skill) => (
                                <span key={skill.id} className="bg-slate-100 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-full">
                                {skill.name}
                                </span>
                            ))}
                            </div>
                        )}
                        <div className="border-t border-slate-200 pt-4 flex justify-between items-center">
                            <p className="text-sm text-slate-500">
                                Posted {getUpdatedText(job.updated)}
                            </p>
                            <Link
                                to={`/job/${job.slug}`}
                                className="font-semibold text-blue-600 hover:text-blue-800 transition-colors text-sm flex items-center gap-1 group-hover:gap-2"
                            >
                                View Details <FiArrowRight className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
                <div className="text-center py-12">
                    <p className="text-lg text-slate-600">No matching job openings found for your search.</p>
                </div>
            )}
          </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <motion.div className="bg-blue-600 p-8 sm:p-12 rounded-2xl text-center text-white" {...fadeIn}>
                <h3 className="text-3xl font-bold mb-4">Don't See Your Role?</h3>
                <p className="max-w-2xl mx-auto mb-6">
                    We're always looking for talented people. If you don't see an open position that fits your skills, send us your resume to be considered for future opportunities.
                </p>
                <Link to="/contact" className="inline-block bg-white text-blue-600 font-semibold py-3 px-6 rounded-lg shadow-sm hover:bg-slate-100 transition-colors transform hover:scale-105">
                    Join Our Talent Pool
                </Link>
            </motion.div>
        </div>
      </section>
    </main>
  );
}

export default DynamicCareer;
